import { useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import type { BuildProgressSnapshot, ChatMessage } from '../api/client'
import { BuildProgressMini } from './BuildProgressMini'
import { SuggestionChips } from './SuggestionChips'

export type UiChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  reviewBuild?: boolean
}

const INTERNAL_MESSAGE_MARKERS = [
  'Continue the active build session now.',
  'Tool call:',
  '[tool result omitted from transcript]',
]

export function messagesFromConversation(
  messages: ChatMessage[],
  options: { reviewLastAssistantBuild?: boolean } = {},
): UiChatMessage[] {
  const visible: UiChatMessage[] = messages.flatMap((message, index) => {
    if (message.role !== 'user' && message.role !== 'assistant') return []
    const content = message.content.trim()
    if (!content) return []
    if (INTERNAL_MESSAGE_MARKERS.some((marker) => content.includes(marker))) return []
    return [
      {
        id: message.id ?? `${message.role}-${message.sequence ?? index}`,
        role: message.role as 'user' | 'assistant',
        content,
      },
    ]
  })

  if (options.reviewLastAssistantBuild) {
    for (let index = visible.length - 1; index >= 0; index -= 1) {
      if (visible[index].role === 'assistant') {
        visible[index] = { ...visible[index], reviewBuild: true }
        break
      }
    }
  }

  return visible
}

export type ChatSuggestion = {
  occasion: string
  quote: string
}

type ChatPanelProps = {
  messages: UiChatMessage[]
  draft: string
  isSending: boolean
  loadingLabel?: string
  buildProgress?: BuildProgressSnapshot | null
  error: string | null
  suggestions?: ChatSuggestion[]
  onSuggestionClick?: (text: string, occasion: string) => void
  onDraftChange: (value: string) => void
  onSubmit: () => void
  onStop: () => void
  onRetry?: () => void
  onReviewBuild?: () => void
  isRestoring?: boolean
  onStartNew?: () => void
}

function autoResize(textarea: HTMLTextAreaElement) {
  textarea.style.height = 'auto'
  textarea.style.height = `${textarea.scrollHeight}px`
}

export function ChatPanel({
  messages,
  draft,
  isSending,
  loadingLabel,
  buildProgress,
  error,
  suggestions = [],
  onSuggestionClick,
  onDraftChange,
  onSubmit,
  onStop,
  onRetry,
  onReviewBuild,
  isRestoring = false,
  onStartNew,
}: ChatPanelProps) {
  const messageListRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const list = messageListRef.current
    if (!list) return
    list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' })
  }, [messages, isSending, loadingLabel, buildProgress])

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    autoResize(textarea)
  }, [draft])

  // Show suggestion chips while greeting state (no user messages yet)
  const showSuggestions =
    suggestions.length > 0 &&
    !isSending &&
    !isRestoring &&
    !messages.some((m) => m.role === 'user')
  const composerDisabled = isSending || isRestoring

  return (
    <section className="chat-panel" aria-labelledby="chat-heading">
      <header className="chat-session">
        <div className="chat-session-row">
          <h2 id="chat-heading" className="chat-session-title">
            Horatix
          </h2>
          {onStartNew ? (
            <button type="button" className="chat-new-build" onClick={onStartNew}>
              New build
            </button>
          ) : null}
        </div>
      </header>

      <div ref={messageListRef} className="chat-area" role="log" aria-live="polite">
        {isRestoring ? (
          <p className="chat-restoring">Restoring your previous build…</p>
        ) : (
          messages.map((message, index) => (
          <article
            key={message.id}
            className={`chat-msg chat-msg--${message.role} chat-msg--enter`}
            style={{ animationDelay: `${Math.min(index * 0.12, 0.5)}s` }}
          >
            <span className="chat-msg-kicker">
              {message.role === 'user' ? 'You' : 'Horatix AI'}
            </span>
            <div className="chat-msg-body">
              <ReactMarkdown
                components={{
                  a: ({ children, ...props }) => (
                    <a {...props} target="_blank" rel="noreferrer">
                      {children}
                    </a>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
              {message.reviewBuild && onReviewBuild ? (
                <button
                  type="button"
                  className="review-build-btn"
                  onClick={onReviewBuild}
                >
                  Review your build
                  <span aria-hidden="true">→</span>
                </button>
              ) : null}
            </div>
          </article>
          ))
        )}

        {isSending ? (
          <article
            className="chat-msg chat-msg--assistant chat-activity"
            aria-hidden="true"
            aria-label={loadingLabel ?? 'Horatix is thinking'}
          >
            <span className="chat-msg-kicker">Horatix</span>
            <div className="chat-msg-body chat-activity-body">
              {buildProgress ? (
                <BuildProgressMini
                  progress={buildProgress}
                  fallbackLabel={loadingLabel}
                  variant="inline"
                />
              ) : (
                <div className="chat-activity-prelude">
                  <span className="chat-typing-dot" />
                  <span className="chat-typing-dot" />
                  <span className="chat-typing-dot" />
                  {loadingLabel ? (
                    <p className="chat-activity-label">{loadingLabel}</p>
                  ) : null}
                </div>
              )}
            </div>
          </article>
        ) : null}
      </div>
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {isSending ? loadingLabel ?? buildProgress?.label ?? 'Horatix is working on your build' : ''}
      </p>

      <div className="chat-input-area">
        {showSuggestions && onSuggestionClick ? (
          <SuggestionChips
            suggestions={suggestions}
            onSelect={onSuggestionClick}
            disabled={isSending}
          />
        ) : null}

        {error ? (
          <div className="chat-error" role="alert">
            <p>{error}</p>
            {onRetry ? (
              <button type="button" className="chat-retry-btn" onClick={onRetry}>
                Retry
              </button>
            ) : null}
          </div>
        ) : null}

        <form
          className="input-row"
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit()
          }}
        >
          <label className="sr-only" htmlFor="watch-prompt">
            Tell Horatix what kind of watch you want
          </label>
          <textarea
            ref={textareaRef}
            id="watch-prompt"
            className="chat-input"
            value={draft}
            onChange={(event) => {
              onDraftChange(event.target.value)
              autoResize(event.target)
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || event.shiftKey) return
              event.preventDefault()
              if (!isSending && draft.trim().length > 0) {
                onSubmit()
              }
            }}
            placeholder="Specify the occasion, style, size, or budget…"
            rows={1}
            disabled={composerDisabled}
          />
          {isSending ? (
            <button
              type="button"
              className="send-btn stop-btn"
              onClick={onStop}
              aria-label="Stop current request"
            >
              Stop
            </button>
          ) : (
            <button
              type="submit"
              className="send-btn"
              disabled={composerDisabled || draft.trim().length === 0}
              aria-label="Send message"
            >
              <svg className="send-arrow" viewBox="0 0 16 16" aria-hidden="true">
                <line x1="2" y1="8" x2="13" y2="8" />
                <polyline points="9,4 13,8 9,12" />
              </svg>
            </button>
          )}
        </form>
      </div>
    </section>
  )
}
